import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Camera, MapPin, Heart, Star, Search, 
  ChevronLeft, ChevronRight, ZoomIn, Download, Share2, 
  Eye, Calendar
} from "lucide-react";

const Gallery = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Sample gallery data (in real app, this would come from API)
  const galleryData = [
    {
      id: 1,
      title: "Taj Mahal at Sunrise",
      location: "Agra, Uttar Pradesh",
      category: "monuments",
      description: "Experience the breathtaking beauty of the Taj Mahal during golden hour",
      image: "/src/assets/images/travel1.jpg",
      photographer: "Aaoghumen Travel Team",
      date: "2024-01-15",
      likes: 1250,
      views: 15400,
      tags: ["heritage", "sunrise", "agra", "monuments", "unesco"]
    },
    {
      id: 2,
      title: "Kerala Backwaters",
      location: "Alleppey, Kerala",
      category: "nature",
      description: "Serene backwaters offering perfect blend of nature and tranquility",
      image: "/src/assets/images/travel2.jpg",
      photographer: "Local Guide Team",
      date: "2024-02-08",
      likes: 980,
      views: 12300,
      tags: ["backwaters", "kerala", "nature", "boats", "peaceful"]
    },
    {
      id: 3,
      title: "Rajasthani Culture",
      location: "Jaisalmer, Rajasthan",
      category: "culture",
      description: "Vibrant cultural performance in the heart of Thar Desert",
      image: "/src/assets/images/travel3.jpg",
      photographer: "Cultural Heritage Team",
      date: "2024-01-22",
      likes: 2100,
      views: 18900,
      tags: ["rajasthan", "culture", "dance", "desert", "traditional"]
    },
    {
      id: 4,
      title: "Himalayan Adventure",
      location: "Manali, Himachal Pradesh",
      category: "adventure",
      description: "Thrilling mountain adventures with stunning Himalayan views",
      image: "/src/assets/images/travel4.jpg",
      photographer: "Adventure Sports Team",
      date: "2024-03-10",
      likes: 1800,
      views: 22100,
      tags: ["himalayas", "adventure", "mountains", "trekking", "snow"]
    },
    {
      id: 5,
      title: "Golden Temple",
      location: "Amritsar, Punjab",
      category: "spiritual",
      description: "Sacred Golden Temple reflecting in holy waters",
      image: "/src/assets/images/travel5.jpg",
      photographer: "Spiritual Journey Team",
      date: "2024-02-18",
      likes: 3200,
      views: 28500,
      tags: ["golden temple", "spiritual", "punjab", "sikh", "heritage"]
    },
    {
      id: 6,
      title: "Goa Beach Sunset",
      location: "Anjuna Beach, Goa",
      category: "beaches",
      description: "Perfect sunset view from the beautiful beaches of Goa",
      image: "/src/assets/images/travel6.jpg",
      photographer: "Coastal Experience Team",
      date: "2024-03-05",
      likes: 1650,
      views: 19200,
      tags: ["goa", "beach", "sunset", "coast", "relaxation"]
    },
    {
      id: 7,
      title: "Mumbai Street Life",
      location: "Mumbai, Maharashtra",
      category: "cities",
      description: "Vibrant street life capturing the essence of Maximum City",
      image: "/src/assets/images/travel1.jpg",
      photographer: "Urban Explorer Team",
      date: "2024-01-30",
      likes: 1420,
      views: 16800,
      tags: ["mumbai", "street", "urban", "culture", "bustling"]
    },
    {
      id: 8,
      title: "Varanasi Ghats",
      location: "Varanasi, Uttar Pradesh",
      category: "spiritual",
      description: "Ancient ghats along the sacred Ganges River",
      image: "/src/assets/images/travel2.jpg",
      photographer: "Heritage Documentation Team",
      date: "2024-02-25",
      likes: 2800,
      views: 24700,
      tags: ["varanasi", "ghats", "ganges", "spiritual", "ancient"]
    }
  ];

  const categories = [
    { id: "all", name: "All", icon: "🌍" },
    { id: "monuments", name: "Monuments", icon: "🏛️" },
    { id: "nature", name: "Nature", icon: "🌿" },
    { id: "culture", name: "Culture", icon: "🎭" },
    { id: "adventure", name: "Adventure", icon: "⛰️" },
    { id: "spiritual", name: "Spiritual", icon: "🕉️" },
    { id: "beaches", name: "Beaches", icon: "🏖️" },
    { id: "cities", name: "Cities", icon: "🏙️" }
  ];

  const filteredImages = galleryData.filter(image => {
    const matchesCategory = selectedCategory === "all" || image.category === selectedCategory;
    const matchesSearch = image.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         image.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         image.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Aaoghumen Travel Gallery",
    "description": "Explore stunning travel photography from across India featuring destinations, culture, and experiences shared by Aaoghumen (Aao Ghumen) travelers and local guides.",
    "url": `${window.location.origin}/gallery`,
    "image": filteredImages.map(img => ({
      "@type": "ImageObject",
      "name": img.title,
      "description": img.description,
      "contentLocation": img.location,
      "creator": img.photographer,
      "dateCreated": img.date,
      "interactionStatistic": [
        {
          "@type": "InteractionCounter",
          "interactionType": "http://schema.org/LikeAction",
          "userInteractionCount": img.likes
        },
        {
          "@type": "InteractionCounter", 
          "interactionType": "http://schema.org/ViewAction",
          "userInteractionCount": img.views
        }
      ]
    }))
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % filteredImages.length 
      : (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImage) {
        if (e.key === 'ArrowRight') {
          const newIndex = (currentIndex + 1) % filteredImages.length;
          setCurrentIndex(newIndex);
          setSelectedImage(filteredImages[newIndex]);
        }
        if (e.key === 'ArrowLeft') {
          const newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
          setCurrentIndex(newIndex);
          setSelectedImage(filteredImages[newIndex]);
        }
        if (e.key === 'Escape') closeLightbox();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, currentIndex, filteredImages]);

  return (
    <>
      <Helmet>
        <title>Aaoghumen Travel Gallery - Stunning India Photography | Aao Ghumen Images</title>
        <meta name="description" content="Explore breathtaking travel photography from Aaoghumen (Aao Ghumen) community - stunning destinations, cultural experiences, monuments & hidden gems across India. View Aao Ghume travel gallery now!" />
        <meta name="keywords" content="aaoghumen gallery, aao ghumen photos, aao ghume images, aaoghume travel photography, india travel photos, destination gallery, travel images india, cultural photography, monument photos, nature photography" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Aaoghumen Travel Gallery - Stunning India Photography" />
        <meta property="og:description" content="Discover breathtaking travel photography from across India - monuments, nature, culture, and adventures captured by Aaoghumen travelers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/gallery`} />
        <meta property="og:image" content={`${window.location.origin}/logo512.png`} />
        <meta property="og:site_name" content="Aaoghumen Gallery" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Aaoghumen Travel Gallery - Stunning India Photography" />
        <meta name="twitter:description" content="Explore amazing travel photography from India's most beautiful destinations." />
        <meta name="twitter:image" content={`${window.location.origin}/logo512.png`} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Aaoghumen Photography Team" />
        <link rel="canonical" href={`${window.location.origin}/gallery`} />
        
        {/* Gallery Schema */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="gallery-pattern" width="30" height="30" patternUnits="userSpaceOnUse">
                  <circle cx="15" cy="15" r="2" fill="currentColor"/>
                  <circle cx="5" cy="5" r="1" fill="currentColor"/>
                  <circle cx="25" cy="25" r="1" fill="currentColor"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#gallery-pattern)" />
            </svg>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28">
            <div className="text-center">
              <div className="mb-6">
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
                  📸 Travel Gallery
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
                <span className="text-yellow-300">Aaoghumen</span>
                <span className="block text-white">Gallery</span>
              </h1>
              
              <p className="text-xl sm:text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-8">
                Discover India's beauty through the lens of <strong>Aao Ghumen</strong> travelers - stunning photography capturing destinations, culture, and unforgettable moments.
              </p>
              
              <div className="flex justify-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-2 flex items-center space-x-4">
                  <Camera className="w-6 h-6 text-yellow-300" />
                  <span className="text-white font-semibold">8,500+ Photos</span>
                  <div className="w-px h-6 bg-white/30"></div>
                  <Star className="w-6 h-6 text-yellow-300" />
                  <span className="text-white font-semibold">500+ Locations</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-white rounded-3xl shadow-lg p-8 -mt-16 relative z-10 border border-gray-100">
            <div className="flex flex-col lg:flex-row gap-6 items-center">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search destinations, tags, or locations..."
                  className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none text-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.icon} {category.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <span className="text-gray-600">
                Showing <strong>{filteredImages.length}</strong> beautiful destinations
              </span>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="max-w-7xl mx-auto px-4 pb-20">
          {filteredImages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
                  onClick={() => openLightbox(image, index)}
                >
                  {/* Image Container */}
                  <div className="relative h-64 overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                      <Camera className="w-16 h-16 text-purple-400" />
                    </div>
                    
                    {/* Overlay on Hover */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="text-center text-white">
                        <ZoomIn className="w-8 h-8 mx-auto mb-2" />
                        <span className="font-semibold">View Image</span>
                      </div>
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-purple-600 capitalize">
                        {image.category}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
                      {image.title}
                    </h3>
                    <div className="flex items-center text-gray-500 text-sm mb-3">
                      <MapPin className="w-4 h-4 mr-2" />
                      {image.location}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {image.description}
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <Heart className="w-4 h-4 mr-1 text-red-400" />
                          {image.likes}
                        </div>
                        <div className="flex items-center">
                          <Eye className="w-4 h-4 mr-1 text-blue-400" />
                          {image.views}
                        </div>
                      </div>
                      <div className="text-xs">
                        {new Date(image.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No images found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <div className="relative max-w-6xl max-h-full bg-white rounded-3xl overflow-hidden">
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors"
              >
                ✕
              </button>
              
              {/* Navigation Buttons */}
              <button
                onClick={() => navigateImage('prev')}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <button
                onClick={() => navigateImage('next')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              <div className="flex flex-col lg:flex-row max-h-[90vh]">
                {/* Image */}
                <div className="lg:flex-1 flex items-center justify-center bg-gray-100 min-h-[400px]">
                  <div className="w-full h-full bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                    <Camera className="w-32 h-32 text-purple-400" />
                  </div>
                </div>
                
                {/* Details */}
                <div className="lg:w-96 p-8 bg-white overflow-y-auto">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">
                    {selectedImage.title}
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-3 text-purple-500" />
                      {selectedImage.location}
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <Camera className="w-5 h-5 mr-3 text-purple-500" />
                      {selectedImage.photographer}
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-5 h-5 mr-3 text-purple-500" />
                      {new Date(selectedImage.date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {selectedImage.description}
                    </p>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{selectedImage.likes}</div>
                        <div className="text-sm text-gray-500">Likes</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{selectedImage.views}</div>
                        <div className="text-sm text-gray-500">Views</div>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Tags:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedImage.tags.map((tag, index) => (
                          <span key={index} className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="pt-6 border-t border-gray-200 space-y-3">
                      <button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-2xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center">
                        <Share2 className="w-5 h-5 mr-2" />
                        Share Image
                      </button>
                      <button className="w-full border-2 border-purple-200 text-purple-600 py-3 rounded-2xl font-semibold hover:bg-purple-50 transition-all duration-300 flex items-center justify-center">
                        <Download className="w-5 h-5 mr-2" />
                        Download HD
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 py-20">
          <div className="max-w-7xl mx-auto px-4 text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Share Your Travel Stories
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed mb-8">
              Join the <strong>Aaoghumen</strong> community and share your amazing travel photography. Help fellow travelers discover incredible destinations through your lens.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/onboarding")}
                className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
              >
                📤 Upload Photos
              </button>
              <button
                onClick={() => navigate("/blog")}
                className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-purple-600 transition-all duration-300 text-lg"
              >
                📖 Read Travel Stories
              </button>
            </div>
          </div>
        </div>

        {/* SEO Content Footer */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Explore India Through Aaoghumen Gallery
              </h3>
              <p className="text-gray-600 text-lg max-w-4xl mx-auto mb-8 leading-relaxed">
                Our <strong>Aaoghumen gallery</strong> showcases the incredible diversity and beauty of India through authentic travel photography. Whether you're searching for "aao ghumen photos", "aao ghume travel images", or "aaoghume destination gallery" - discover breathtaking monuments, vibrant cultures, serene nature, spiritual experiences, and thrilling adventures captured by our community of passionate travelers and local guides. Let these stunning visuals inspire your next journey with <strong>Aao Ghume</strong>.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">8,500+</div>
                  <div className="text-gray-600">Travel Photos</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">500+</div>
                  <div className="text-gray-600">Destinations Covered</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">1,000+</div>
                  <div className="text-gray-600">Contributing Photographers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">2M+</div>
                  <div className="text-gray-600">Gallery Views Monthly</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Gallery;